import { Component, OnInit } from "@angular/core";
import {
  SharedModule,
  TypedBaseListComponent,
  DataService,
  ModalService,
  NotificationService,
  I18nService,
} from "@bb-vendure/admin-ui/core";
import {
  ApproveRequestPurchaseDocument,
  FetchRequestPurchaseGroupsDocument,
  RequestToApproveRequestPurchaseDocument,
} from "src/generated-gql/graphql";
import { take } from "rxjs";
import { ApproveRequestDialogComponent } from "../dialog/approve-dialog.component";
import { CreateRequestDialogComponent } from "../create-request-dialog/request-dialog/create-request-dialog.component";

@Component({
  standalone: true,
  imports: [SharedModule],
  selector: "request-purchase-table",
  styleUrl: "./request-purchase-table.component.scss",
  templateUrl: "./request-purchase-table.component.html",
})
export class RequestPurchaseTableComponent extends TypedBaseListComponent<
  typeof FetchRequestPurchaseGroupsDocument,
  "requestPurchaseGroups"
> {
  isLoading = false;
  currentTabStatus = "Requested"; // 현재 탭의 상태

  constructor(
    protected dataService: DataService,
    protected modalService: ModalService,
    protected notificationService: NotificationService,
    protected translateService: I18nService
  ) {
    super();

    this.route.url.pipe(take(1)).subscribe((res) => {
      let stateFilter = "Requested";
      if (res.length > 0) {
        const url = res[0];
        if (url.path.includes("approved")) {
          stateFilter = "Approved";
        }
        if (url.path.includes("rejected")) {
          stateFilter = "Rejected";
        }
        if (url.path.includes("pending-approval")) {
          stateFilter = "PendingApproval";
        }
      }
      
      this.currentTabStatus = stateFilter; // 현재 탭 상태 저장

      super.configure({
        document: FetchRequestPurchaseGroupsDocument,
        getItems: (data) => {
          return data.requestPurchaseGroups;
        },
        setVariables: (skip, take) => ({
          options: {
            skip,
            take,
            filter: {
              status: {
                eq: stateFilter,
              },

              ...this.filters.createFilterInput(),
            },
            sort: this.sort.createSortInput(),
          },
        }),
        refreshListOnChanges: [
          this.filters.valueChanges,
          this.sort.valueChanges,
        ],
      });
    });

    // 필터 변경 시 로그만 출력
    this.filters.valueChanges.pipe().subscribe((res) => {
      console.log("filter", res);
    });
  }

  readonly filters = this.createFilterCollection()
    .addIdFilter()
    .addDateFilters()
    .addFilter({
      name: "status",
      label: "Status",
      filterField: "status",
      type: {
        kind: "select",
        options: [
          {
            value: "Requested",
            label: this.translateService.translate("requestPurchase.Requested"),
          },
          {
            value: "Created",
            label: this.translateService.translate("requestPurchase.Created"),
          },
          {
            value: "PendingApproval",
            label: this.translateService.translate("requestPurchase.PendingApproval"),
          },
          {
            value: "Approved",
            label: this.translateService.translate("requestPurchase.Approved"),
          },
          {
            value: "Rejected",
            label: this.translateService.translate("requestPurchase.Rejected"),
          },
          {
            value: "Completed",
            label: this.translateService.translate("requestPurchase.Completed"),
          },
        ],
      },
    })
    .connectToRoute(this.route);
  readonly sort = this.createSortCollection().defaultSort("createdAt", "DESC");

  request() {
    console.log(this.selectionManager.selection);
  }

  approve() {
    //const requests: { id: string }[] = this.selectionManager.selection;

    const groups = this.selectionManager.selection;

    this.modalService
      .fromComponent(ApproveRequestDialogComponent, {
        closable: true,
        size: "xl",
        locals: {
          selectedGroup: groups,
        },
      })
      .pipe(take(1))
      .subscribe((result) => {
        if (result) {
          this.notificationService.info("Sending requests...");
          this.isLoading = true;

          this.dataService
            .mutate(RequestToApproveRequestPurchaseDocument, {
              input: Object.values(result).map((g: any) => {
                return {
                  id: g.id,
                  expectedDate: new Date(g.expectedDate).toISOString(),
                  note: g.note,
                  shopSupplierId: g.supplier?.id ?? null,
                  retailerId: g.retailer?.id ?? null,
                  imageIds: g.imageIds,
                  requests: g.requests.map((r) => {
                    return {
                      id: r.id,
                      expectedDate: new Date(r.expectedDate).toISOString(),
                    };
                  }),
                };
              }),
              // isApproved: true,
            })
            .pipe(take(1))
            .subscribe(({ requestToApprovePurchaseNeeded }) => {
              //console.log(approvePurchaseNeeded);
              this.notificationService.success(
                "Request to approve successfully"
              );
              this.refresh();
              this.isLoading = false;
              this.selectionManager.clearSelection();
            });
        }
      });
  }

  reject() {
    const groups = this.selectionManager.selection;
    this.notificationService.info("Sending requests...");
    this.isLoading = true;

    this.dataService
      .mutate(ApproveRequestPurchaseDocument, {
        input: groups.map((g) => {
          return {
            id: g.id,
            expectedDate: new Date(g.expectedDate).toISOString(),
            note: g.note,
            shopSupplierId: null,
            retailerId: null,
          };
        }),
        isApproved: false,
      })
      .pipe(take(1))
      .subscribe(({ approvePurchaseNeeded }) => {
        console.log(approvePurchaseNeeded);
        this.notificationService.success("Reject successfully");
        this.refresh();
        this.isLoading = false;
        this.selectionManager.clearSelection();
      });
  }

  finalApprove() {
    const groups = this.selectionManager.selection;
    console.log("Selected groups for approval:", groups); // 디버그 로그
    
    if (!groups || groups.length === 0) {
      this.notificationService.error("No items selected");
      return;
    }
    
    this.notificationService.info("Approving requests...");
    this.isLoading = true;

    try {
      const input = groups.map((g) => {
        console.log("Processing group:", g); // 각 그룹 로그
        
        // 안전한 이미지 ID 추출
        let imageIds = [];
        try {
          if (g.images && Array.isArray(g.images)) {
            imageIds = g.images.map(img => img?.id).filter(id => id);
          }
        } catch (imgError) {
          console.warn("Error processing images for group", g.id, imgError);
          imageIds = [];
        }
        
        return {
          id: g.id,
          expectedDate: g.expectedDate ? new Date(g.expectedDate).toISOString() : new Date().toISOString(),
          note: g.note || "",
          shopSupplierId: g.shopSupplierId || null,
          retailerId: g.retailerId || null,
          imageIds: imageIds,
        };
      });
      
      console.log("Final input for mutation:", input);

      this.dataService
        .mutate(ApproveRequestPurchaseDocument, {
          input: input,
          isApproved: true,
        })
        .pipe(take(1))
        .subscribe({
          next: ({ approvePurchaseNeeded }) => {
            console.log(approvePurchaseNeeded);
            this.notificationService.success("Approve successfully");
            this.refresh();
            this.isLoading = false;
            this.selectionManager.clearSelection();
          },
          error: (error) => {
            this.isLoading = false;
          }
        });
    } catch (error) {
      this.isLoading = false;
    }
  }

  status(value: string) {
    return this.translateService.translate(`requestPurchase.${value}`);
  }

  createRequest() {
    this.modalService
      .fromComponent(CreateRequestDialogComponent, {
        size: "xl",
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.notificationService.success("Create new request successfully");
          this.refresh();
        }
      });
  }
}
